import {
  takeLatest,
  call,
  put,
  select,
} from 'redux-saga/effects';
import React from 'react';
import _ from 'lodash';
import { createFirmService, checkFirmNameExistsService } from '../../../services/firms';
import * as actions from '../firmsActions';
import { openSnackbar } from '../../_shared/snack-bar/snackbarActions';
import { errorOpenModal } from '../../_shared/modal/errorModalActions';
import { openModal } from '../../_shared/modal/modalActions';
import DuplicateFirmConfirmationContent from '../externalFirms/DuplicateFirmConfirmationContent';
import { sanitizeTextInput } from '../firmUtils';
import { FIRM_FIELD_LIMITS } from '../firmConstants';

const formValueSelector = state => _.get(state, 'form.createFirm.values');

export function* createFirmWorker(action) {
  yield put(actions.loading(true));
  try {
    const { history, bypassDuplicateCheck } = action;
    const values = yield select(formValueSelector);

    if (!values) {
      throw new Error('Firm form data is missing. Please fill all required fields and try again.');
    }

    const firmName = sanitizeTextInput(values.extrn_company_name);
    if (firmName.length === 0) {
      throw new Error('Firm Name is required');
    }
    if (firmName.length > FIRM_FIELD_LIMITS.FIRM_NAME) {
      throw new Error(`Firm Name should have ${FIRM_FIELD_LIMITS.FIRM_NAME} or less characters`);
    }

    const addrLine1 = sanitizeTextInput(values.extrn_addr_line1);
    if (addrLine1.length === 0) {
      throw new Error('Address Line 1 is required');
    }
    if (addrLine1.length > FIRM_FIELD_LIMITS.ADDRESS_LINE) {
      throw new Error(`Address Line 1 should have ${FIRM_FIELD_LIMITS.ADDRESS_LINE} or less characters`);
    }

    const addrLine2 = sanitizeTextInput(values.extrn_addr_line2);
    if (addrLine2.length > FIRM_FIELD_LIMITS.ADDRESS_LINE) {
      throw new Error(`Address Line 2 should have ${FIRM_FIELD_LIMITS.ADDRESS_LINE} or less characters`);
    }

    const cityName = sanitizeTextInput(values.extrn_city_name);
    if (cityName.length === 0) {
      throw new Error('City is required');
    }
    if (cityName.length > FIRM_FIELD_LIMITS.CITY) {
      throw new Error(`City should have ${FIRM_FIELD_LIMITS.CITY} or less characters`);
    }

    const stProvCd = sanitizeTextInput(values.extrn_st_prov_cd);
    if (stProvCd.length === 0) {
      throw new Error('State is required');
    }
    if (!/^[A-Za-z]{2}$/.test(stProvCd)) {
      throw new Error('State input value should be a two-letter abbreviation');
    }

    const postalCd = sanitizeTextInput(values.extrn_postal_cd);
    if (postalCd.length === 0) {
      throw new Error('Zip Code is required');
    }
    if (postalCd.length > FIRM_FIELD_LIMITS.ZIP) {
      throw new Error(`Zip Code should have ${FIRM_FIELD_LIMITS.ZIP} or less characters`);
    }
    if (!/^[0-9-]*$/.test(postalCd)) {
      throw new Error('Zip Code can only contain numbers and hyphens');
    }

    const firmData = {
      extrn_company_name: firmName,
      extrn_addr_line1: addrLine1,
      extrn_addr_line2: addrLine2.length > 0 ? addrLine2 : null,
      extrn_city_name: cityName,
      extrn_st_prov_cd: stProvCd,
      extrn_postal_cd: postalCd,
    };

    if (!bypassDuplicateCheck) {
      const duplicateCheckResponse = yield call(checkFirmNameExistsService, {
        extrn_company_name: firmName,
      });

      const isDuplicateFirm = _.get(duplicateCheckResponse, 'data.checkFirmNameExists.exists', false);
      if (isDuplicateFirm) {
        const duplicateMessage = _.get(
          duplicateCheckResponse,
          'data.checkFirmNameExists.message',
          'Firm name already exist in the system, do you wish to proceed?',
        );

        yield put(actions.loading(false));
        yield put(
          openModal({
            body: (
              <DuplicateFirmConfirmationContent
                message={duplicateMessage}
                proceedAction={actions.createFirm(history, true)}
              />
            ),
          }),
        );
        return;
      }
    }

    const response = yield call(createFirmService, firmData);
    yield call(success, response, history);
  } catch (error) {
    yield call(failure, error);
  }
}

export function* success(response, history) {
  yield put(actions.loading(false));

  const data = _.get(response, 'data.createFirm', {
    Success: false,
    message: 'Firm could not be created. Please try again.',
    extrn_company_id: null,
  });

  const isSuccess = data.Success === true || data.Success === 'true';
  const isInsertExecuted = !_.isNil(data.extrn_company_id);

  if (isSuccess && isInsertExecuted) {
    const message = _.get(data, 'message', 'Firm created successfully');
    history.replace('/firms');
    yield put(
      openSnackbar({
        message,
        showCloseIcon: true,
      }),
    );
  } else {
    const failureMessage = _.get(data, 'message', 'Firm could not be created. Insert statement did not complete successfully.');
    yield call(failure, new Error(failureMessage));
  }
}

export function* failure(error) {
  yield put(actions.loading(false));
  yield put(
    errorOpenModal({
      title: 'Firm could not be created',
      message: 'Unable to create firm. Please try again or contact support if the issue persists.',
    }),
  );
  yield put(actions.error(error));
}

export default function* createFirmWatcher() {
  yield takeLatest(actions.CREATE_FIRM, createFirmWorker);
}
